import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  initializeUTMTracking,
  parseUTMsFromURL,
  saveFirstTouchUTMs,
  saveLatestTouchUTMs,
  getEffectiveUTMs,
  getFirstTouchUTMs,
  getLatestTouchUTMs,
  getAttributionPayload,
  appendUTMsToInternalLink,
  hasUTMData,
  type UTMData,
  type AttributionPayload,
} from '@/lib/utm-tracking';
import { captureAdClickIds } from '@/lib/ad-click-tracking';

interface UTMContextValue {
  /** Get effective UTMs (URL > latest > first touch) */
  getEffectiveUTMs: () => UTMData;
  /** Get first-touch UTMs */
  getFirstTouchUTMs: () => UTMData;
  /** Get latest-touch UTMs */
  getLatestTouchUTMs: () => UTMData;
  /** Get full attribution payload for form submission */
  getAttributionPayload: () => AttributionPayload;
  /** Append UTMs to an internal link */
  appendUTMsToLink: (href: string) => string;
  /** Check if there are any effective UTMs */
  hasUTMs: () => boolean;
}

const UTMContext = createContext<UTMContextValue | null>(null);

interface UTMProviderProps {
  children: ReactNode;
}

/**
 * UTM Provider Component
 * 
 * Wraps the app to provide UTM tracking context.
 * Automatically initializes tracking and updates on route changes.
 * 
 * @example
 * ```tsx
 * // In App.tsx or main.tsx
 * <BrowserRouter>
 *   <UTMProvider>
 *     <App />
 *   </UTMProvider>
 * </BrowserRouter>
 * ```
 */
export function UTMProvider({ children }: UTMProviderProps) {
  const location = useLocation();

  // Initialize tracking on mount
  useEffect(() => {
    initializeUTMTracking();
    captureAdClickIds();
  }, []);

  // Re-process UTMs when URL changes
  useEffect(() => {
    const urlUTMs = parseUTMsFromURL();
    
    if (hasUTMData(urlUTMs)) {
      // Save as first touch if not already set
      saveFirstTouchUTMs(urlUTMs);
      // Always update latest touch
      saveLatestTouchUTMs(urlUTMs);
    }

    // Capture ad click IDs (gclid/fbclid/msclkid) → 90-day cookie
    captureAdClickIds();
  }, [location.search, location.pathname]);

  const value: UTMContextValue = {
    getEffectiveUTMs,
    getFirstTouchUTMs,
    getLatestTouchUTMs,
    getAttributionPayload,
    appendUTMsToLink: appendUTMsToInternalLink,
    hasUTMs: () => hasUTMData(getEffectiveUTMs()),
  };

  return (
    <UTMContext.Provider value={value}>
      {children}
    </UTMContext.Provider>
  );
}

/**
 * Hook to access UTM context
 * 
 * @throws Error if used outside UTMProvider
 * 
 * @example
 * ```tsx
 * const { getAttributionPayload, appendUTMsToLink } = useUTM();
 * 
 * // In form submission:
 * const attribution = getAttributionPayload();
 * submitForm({ ...formData, ...attribution });
 * 
 * // For links:
 * <Link to={appendUTMsToLink('/contact')}>Get a Quote</Link>
 * ```
 */
export function useUTM(): UTMContextValue {
  const context = useContext(UTMContext);
  
  if (!context) {
    throw new Error('useUTM must be used within a UTMProvider');
  }
  
  return context;
}

export default UTMProvider;
